import { useMemo } from 'react';
import {
  CatchNotFound,
  createFileRoute,
  Navigate,
  Outlet,
  useParams,
} from '@tanstack/react-router';
import { z } from 'zod';

import { useClubRoles } from '@features/auth/hooks/useClubRoles';
import { NotFoundPage } from '@features/auth/pages/NotFoundPage';
import { parseRoomsSearch, roomKey } from '@features/video/lib/ticket';
import { RefereeRoomPageWidget } from '@features/video/pages/RefereeRoomPage/RefereeRoomPageWidget';
import { RoomRef } from '@features/video/types';

// Остальные комнаты мультикомнаты: `<tournamentId>:<номер>` через запятую
const querySchema = z.object({
  rooms: z.string().optional(),
});

// Страница судьи в видеокомнате турнира. Дочерний роут `private/$userId` только
// несет параметр: страница с подключением живет здесь и переходы ее не перемонтируют.
// Мультикомната: первая комната — в пути, остальные — в search `rooms`
export const Route = createFileRoute(
  '/club/$clubId_/tournament/$tournamentId_/referee/$roomNumber',
)({
  component: () => (
    <CatchNotFound fallback={() => <NotFoundPage subTitle="Турнир не найден" />}>
      <Page />
    </CatchNotFound>
  ),
  validateSearch: querySchema,
});

function Page() {
  const { clubId, tournamentId, roomNumber } = Route.useParams();
  const { rooms: roomsSearch } = Route.useSearch();
  const { userId } = useParams({ strict: false });
  const { isManager } = useClubRoles(clubId);

  const number = Number(roomNumber);
  const rooms = useMemo<RoomRef[]>(() => {
    const first: RoomRef = { tournamentId, roomNumber: number };
    const seen = new Set([roomKey(first)]);
    const rest = parseRoomsSearch(roomsSearch).filter((room) => {
      const key = roomKey(room);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return [first, ...rest];
  }, [tournamentId, number, roomsSearch]);

  if (!isManager) {
    return (
      <Navigate to="/club/$clubId/tournament/$tournamentId" params={{ clubId, tournamentId }} />
    );
  }

  if (!Number.isInteger(number) || number < 1) {
    return <NotFoundPage subTitle="Комната не найдена" />;
  }

  return (
    <>
      <RefereeRoomPageWidget clubId={clubId} rooms={rooms} privateUserId={userId} />
      <Outlet />
    </>
  );
}
